import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EligibilitySessionDto, NphiesEligibilityResultDto } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Alert, Button, Field } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../../patients/PatientPicker';
import { SessionChip } from './PatientInsuranceSections';
import { MessagesButton } from './MessagesDrawer';
import { limitKey } from './eligibility-session';
import { ExpireEligibilityDialog, useEligibilitySession } from './EligibilityPanel';

export function EligibilityTab() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [patientId, setPatientId] = useState('');
  const [expiring, setExpiring] = useState<EligibilitySessionDto | null>(null);
  const sessions = useEligibilitySession(patientId || null, undefined, true);
  const payers = useQuery({ queryKey: ['payers'], queryFn: () => get<{ id: string; name: string }[]>('/insurance/payers'), staleTime: 60_000 });
  const payerName = new Map((payers.data ?? []).map((payer) => [payer.id, payer.name]));
  const mayAsk = can('nphies.eligibility');
  const mayExpire = can('coverage.verify', 'nphies.eligibility');

  const check = useMutation({
    mutationFn: (session: EligibilitySessionDto) => post<NphiesEligibilityResultDto>('/integrations/nphies/eligibility', { patientId: session.patientId, coverageId: session.coverageId, reuseSession: true }),
    onSuccess: (answer) => {
      if (answer.eligible) toast.success(answer.sessionReused ? tr('billing', 'nx_session_reused') : answer.message, answer.warnings.join(' ') || undefined);
      else toast.error(answer.message, answer.warnings.join(' ') || undefined);
      void queryClient.invalidateQueries({ queryKey: ['eligibility-sessions'] });
    },
    onError: (caught) => toast.error(tr('billing', 'nx_eligibility_check_failed'), describeError(caught)),
  });

  return (
    <div className="space-y-3">
      <Field label={tr('billing', 'nx_pick_patient_for_sessions')} className="max-w-md">
        <PatientPicker value={patientId} onChange={setPatientId} />
      </Field>
      {!patientId ? (
        <Alert>{tr('billing', 'nx_pick_patient_hint')}</Alert>
      ) : (
        <DataTable
          rows={sessions.data?.items}
          loading={sessions.isLoading}
          refreshing={sessions.isFetching}
          error={sessions.error}
          rowKey={(row) => row.coverageId}
          empty={tr('billing', 'nx_no_sessions')}
          columns={[
            { header: tr('billing', 'payer'), cell: (row) => payerName.get(row.payerId) ?? '—' },
            { header: tr('billing', 'nx_eligibility'), cell: (row) => <SessionChip session={row} /> },
            { header: tr('billing', 'nx_checked'), cell: (row) => (row.checkedAt ? <span className="whitespace-nowrap text-xs">{dateTime(row.checkedAt)}</span> : '—') },
            {
              header: tr('billing', 'nx_limited_by'),
              cell: (row) => {
                const key = row.state === 'valid' ? limitKey(row.limitedBy) : null;
                return key ? <span className="text-xs text-muted-foreground">{tr('billing', key, { days: row.validDays })}</span> : '—';
              },
            },
            { header: tr('billing', 'nx_response_id'), cell: (row) => <span className="font-mono text-xs">{row.responseId ?? '—'}</span> },
            {
              header: '',
              align: 'right',
              cell: (row) => (
                <span className="flex justify-end gap-1">
                  {mayAsk && (
                    <Button size="sm" variant="outline" loading={check.isPending && check.variables?.coverageId === row.coverageId} onClick={() => check.mutate(row)}>
                      {tr('billing', 'nx_check_eligibility')}
                    </Button>
                  )}
                  {mayExpire && row.state === 'valid' && (
                    <Button size="sm" variant="ghost" onClick={() => setExpiring(row)}>
                      {tr('billing', 'nx_expire_now')}
                    </Button>
                  )}
                  <MessagesButton entityType="patient_coverage" entityId={row.coverageId} title={tr('billing', 'nx_messages_for_eligibility')} />
                </span>
              ),
            },
          ]}
        />
      )}
      {expiring && (
        <ExpireEligibilityDialog
          coverageId={expiring.coverageId}
          onClose={() => setExpiring(null)}
          onDone={() => {
            setExpiring(null);
            void queryClient.invalidateQueries({ queryKey: ['eligibility-sessions'] });
          }}
        />
      )}
    </div>
  );
}
